import { useEffect, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuthStore } from "@/stores/authStore";
import { useWorkspaceStore } from "@/stores/workspaceStore";
import {
  dispatchRealtimeEvent,
  dispatchWorkshopLiveFrame,
  openRealtimeConnection,
  subscribeToBrand,
  useRealtimeStore,
} from "@/lib/socket";
import {
  REALTIME_EVENT,
  WORKSHOP_LIVE_EVENT,
  type RealtimeEnvelope,
  type WorkshopLiveFrame,
} from "@/types/realtime.type";
import {
  applyRealtimeEvent,
  resyncBrand,
} from "@/app/routes/dashboard/api/realtimeHandlers";
import { applyWorkshopLiveFrame } from "@/app/routes/dashboard/workshop/api/workshopRealtime";

/**
 * Owns the tab's one realtime connection for as long as someone is signed in,
 * and keeps it in the selected brand's room. Mounted under ProtectedLayout, so
 * the outage page (which replaces the whole tree) closes it too.
 */
export default function RealtimeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  // Keyed on being signed in, not on the token: a rotation must not reconnect.
  const signedIn = useAuthStore((s) => !!s.token);
  const brandId = useWorkspaceStore((s) => s.selectedBrandId);
  const socket = useRealtimeStore((s) => s.socket);

  useEffect(() => {
    if (!signedIn) return;
    return openRealtimeConnection();
  }, [signedIn]);

  useEffect(() => {
    if (!socket) return;
    const onEvent = (event: RealtimeEnvelope) => {
      applyRealtimeEvent(queryClient, event);
      dispatchRealtimeEvent(event);
    };
    // A shared workshop thread's live signals: presence patches the lists
    // here; the open thread follows the rest through useWorkshopLiveFrame.
    const onWorkshopLive = (frame: WorkshopLiveFrame) => {
      applyWorkshopLiveFrame(queryClient, frame);
      dispatchWorkshopLiveFrame(frame);
    };
    socket.on(REALTIME_EVENT, onEvent);
    socket.on(WORKSHOP_LIVE_EVENT, onWorkshopLive);
    return () => {
      socket.off(REALTIME_EVENT, onEvent);
      socket.off(WORKSHOP_LIVE_EVENT, onWorkshopLive);
    };
  }, [socket, queryClient]);

  useEffect(() => {
    if (!socket || !brandId) return;
    return subscribeToBrand(socket, brandId, (since) =>
      resyncBrand(queryClient, brandId, since),
    );
  }, [socket, brandId, queryClient]);

  return children;
}
